import {
  Units, makeValStr, getMetricUnits,
  valToStr, roundToNumDecimals,
} from './Units.js';
import { gApp } from '../Globals.js'
import { ProjectUnits } from './Field.js';
import { setupClass } from '../Base.js';
import { valOr } from '../SharedUtils.js';

/**
 * Helper class to represent a quantity with value and units.
 * Contains helper methods to convert a quantity to project/display units.
 */
export class Quantity {
  init(value, units, opts) {
    this.value = value;
    this.units = units;
    this.opts = opts ||  {};
  }

  static getProjectUnits() {
    let projectUnits = ProjectUnits.Imperial;
    if (gApp && gApp.proj()) {
      projectUnits = gApp.proj().getProjectUnits();
    }
    return projectUnits;
  }

  static getConvertedUnits(units, opts) {
    let projectUnits = opts?.projectUnits || Quantity.getProjectUnits();
    if (projectUnits === ProjectUnits.Metric) {
      return getMetricUnits(units);
    } else {
      return units;
    }
  }

  static makeInProjectUnits(value, units) {
    let targetUnits = Quantity.getConvertedUnits(units);
    let convertedValue = Units.convertValue(value, units, targetUnits);
    return Quantity.create(convertedValue, targetUnits);
  }

  static convertValue(value, fromUnits, toUnits) {
    return Units.convertValue(value, fromUnits, toUnits);
  }

  convertToUnits(targetUnits) {
    let convertedValue = Units.convertValue(this.value, this.units, targetUnits);
    return Quantity.create(convertedValue, targetUnits);
  }

  convertToProjectUnits(opts) {
    let convertedUnits = Quantity.getConvertedUnits(this.units, opts);
    return this.convertToUnits(convertedUnits);
  }

  static convertToProjectUnits(value, units, opts) {
    let quantity = Quantity.create(value, units);
    return quantity.convertToProjectUnits(opts);
  }

  getDisplayUnitsLabel() {
    return Units.getLabel(Quantity.getConvertedUnits(this.units));
  }

  makeValStr(opts) {
    /**
     * NOTE - this produces a string in the project/display units
     */
    opts = valOr(opts, {});
    let projectQuantity = this.convertToProjectUnits(opts);
    return makeValStr(projectQuantity.value, projectQuantity.units, opts);
  }

  static makeValStr(value, units, opts) {
    opts = valOr(opts, {});
    return Quantity.create(value, units).makeValStr(opts);
  }

  toStrNoUnits(opts) {
    let valToStrOpts = {
      precision: Units.getDefaultPrecision(this.units),
      ...opts,
    }
    return valToStr(this.value, valToStrOpts);
  }

  static convertArrayToUnits(values, fromUnits, toUnits) {
    return values.map(v => {
      return Units.convertValue(v, fromUnits, toUnits);
    });
  }

  static roundToPrecision(value, precision) {
    return roundToNumDecimals(value, precision);
  }

  static getPrecisionForUnits(units) {
    return Units.getDefaultPrecision(units);
  }
};
setupClass(Quantity);